// API POS V2.1 – Bán hàng & Kiểm soát công nợ (tài liệu: "POS Bán Hàng & Kiểm Soát Công Nợ (V2)")
// Response chuẩn: { status: <mã HTTP>, success: boolean, message, data } — luôn kiểm tra `success`, không dùng `status`.
// Lỗi nghiệp vụ trả HTTP 422 kèm `code` (DISCOUNT_APPROVAL_REQUIRED, INVALID_FINAL_AMOUNT, ...).
// Ngày giờ: mỗi trường hiển thị ("HH:mm DD/MM/YYYY") có bản ISO 8601 đi kèm (`*_iso`) — ưu tiên dùng bản ISO.
import { authFetch } from '../services/mixin';

/* ================= TYPES ================= */

/** EWALLET: ví điện tử ngoài (MoMo, ZaloPay...). PREPAID: ví trả trước của khách, server trừ số dư. WALLET: tên cũ (trước V2.1) */
export type PosPaymentMethod = 'CASH' | 'BANK_TRANSFER' | 'POS_CARD' | 'EWALLET' | 'PREPAID' | 'WALLET';
export type PosStatus = 'WAITING_PAYMENT' | 'COMPLETED_PAYMENT' | 'IN_PROGRESS' | string;
export type PosPaymentStatus = 'UNPAID' | 'PARTIALLY_PAID' | 'PAID' | string;
/** FULL: trả đủ · DEPOSIT: đặt cọc / còn nợ · INSTALLMENT: trả góp theo đợt */
export type PosPaymentType = 'FULL' | 'DEPOSIT' | 'INSTALLMENT' | string;

export type PosErrorCode =
    | 'DISCOUNT_APPROVAL_REQUIRED'
    | 'INVALID_FINAL_AMOUNT'
    | 'INSUFFICIENT_WALLET_BALANCE'
    | 'DEBT_GUARANTEE_REQUIRED'
    | 'GUARANTOR_NOT_AUTHORIZED'
    | 'APPOINTMENT_ALREADY_CHECKED_OUT'
    | 'APPOINTMENT_NOT_READY'
    | 'INSTALLMENT_FIRST_TOO_LOW'
    | 'INSTALLMENT_PLAN_INVALID'
    | 'INSTALLMENT_PLAN_MISMATCH'
    | 'PAYMENT_EXCEEDS_DEBT'
    | 'CUSTOMER_REQUIRED'
    | 'FULL_PAYMENT_REQUIRED'
    | 'PRODUCT_INACTIVE'
    | 'PRODUCT_NOT_FOUND'
    | 'PAYMENT_EXCEEDS_TOTAL'
    | string;

/* ---------- Trả góp (V2.1) ---------- */

/** Một đợt trả góp của đơn */
export interface PosInstallment {
    id: number;
    no: number;
    /** YYYY-MM-DD */
    due_date: string;
    amount: number;
    paid_amount: number;
    remaining: number;
    status: 'PAID' | 'PENDING' | 'OVERDUE' | string;
    overdue_days: number;
}

/** Đợt kế tiếp chưa thu (server chỉ trả số đợt, hạn, còn lại) */
export type PosNextInstallment = Pick<PosInstallment, 'no' | 'due_date' | 'remaining'>;

/** Khoản vừa thu được phân bổ vào đợt nào (server phân bổ theo thứ tự đợt) */
export interface PosInstallmentAllocation {
    installment_id: number;
    installment_no: number;
    amount: number;
}

/** Các trường trả góp đi kèm chi tiết đơn / checkout / thu tiền */
export interface PosInstallmentInfo {
    payment_type?: PosPaymentType;
    /** Nợ ĐẾN HẠN (đợt đã tới ngày mà chưa trả) – khác debt_amount là tổng nợ còn lại */
    due_amount?: number;
    /** YYYY-MM-DD */
    next_due_date?: string | null;
    next_installment?: PosNextInstallment | null;
    installments?: PosInstallment[];
}

/** Đợt gửi lên khi tạo đơn trả góp: đợt 1 = hôm nay, ≥ 30% tổng đơn; tối đa 12 đợt trong 12 tháng; tổng = final_amount */
export interface PosInstallmentInput {
    /** YYYY-MM-DD */
    due_date: string;
    amount: number;
}

export interface PosResult<T> {
    ok: boolean;
    data: T | null;
    message: string;
    /** Mã lỗi nghiệp vụ khi thất bại */
    code?: PosErrorCode;
    /** Toàn bộ body lỗi (vd. require_role, calculated_final, debt_amount) */
    error?: Record<string, any> | null;
}

/** 1. Lịch hẹn S7 chờ thanh toán (cột trái POS) */
export interface PosWaitingOrder {
    appointment_id: number;
    appointment_code: string;
    customer_id: number | null;
    customer_name: string | null;
    phone: string | null;
    tag_id: number;
    tag_name: string;
    branch_id: number | null;
    branch_name: string | null;
    consultant_name: string | null;
    /** "HH:mm DD/MM/YYYY" */
    scheduled_at: string | null;
    scheduled_at_iso?: string | null;
    /** "HH:mm:ss DD/MM/YYYY" */
    waiting_since: string | null;
    waiting_since_iso?: string | null;
    primary_service: string | null;
    total_amount: number;
    items_count: number;
    status: string;
    status_label: string;
    /** ĐỀ XUẤT – API chưa có: loại hàng trong lịch hẹn, để POS lọc tab chờ thanh toán */
    item_types?: ('service' | 'product' | 'package')[];
}

export interface PosCustomer {
    id: number;
    name: string;
    phone: string | null;
    email?: string | null;
    address?: string | null;
    rank?: string | null;
    current_debt?: number;
    /** Số dư ví trả trước (có ở chi tiết đơn 2b) */
    wallet_balance?: number | null;
}

export interface PosCartItem {
    /** Có ở giỏ lịch hẹn (2) */
    service_id?: number;
    /** Có ở chi tiết đơn (2b) */
    id?: number;
    product_code: string | null;
    product_name: string;
    product_category?: string | null;
    quantity: number;
    price: number;
    /** Giảm trên MỖI đơn vị: sub_total = (price − discount) × quantity */
    discount: number;
    sub_total: number;
    staff_id: number | null;
    staff_name: string | null;
}

/** 2. Giỏ hàng lịch hẹn */
export interface PosAppointmentCart {
    appointment_id: number;
    appointment_code: string;
    consultant_name: string | null;
    customer: PosCustomer | null;
    branch: { id: number; name: string } | null;
    items: PosCartItem[];
    subtotal: number;
    discount_amount: number;
    final_amount: number;
    tag_name: string;
    note: string | null;
}

export interface PosOrderPayment {
    id: number;
    payment_method: PosPaymentMethod;
    amount: number;
    is_deposit: boolean;
    transaction_code: string | null;
    cashier_name: string | null;
    /** "HH:mm:ss DD/MM/YYYY" */
    created_at: string | null;
    created_at_iso?: string | null;
}

/** 2b. Chi tiết đơn POS */
export interface PosOrderDetail extends PosInstallmentInfo {
    /** Quản lý duyệt kế hoạch trả góp có đợt 1 < 30% */
    plan_approved_by?: number | null;
    order_id: number;
    order_code: string;
    appointment_id: number | null;
    customer: PosCustomer | null;
    branch: { id: number; name: string } | null;
    items: PosCartItem[];
    subtotal: number;
    discount_amount: number;
    voucher_code: string | null;
    voucher_discount: number;
    order_discount: number;
    rounding_amount: number;
    final_amount: number;
    paid_amount: number;
    debt_amount: number;
    pos_status: PosStatus;
    payment_status: PosPaymentStatus;
    approved_by: number | null;
    approver_name: string | null;
    approved_note: string | null;
    payments: PosOrderPayment[];
    note: string | null;
    order_date?: string | null;
    order_date_iso?: string | null;
    created_at_iso?: string | null;
}

export interface PosUpdateServicesBody {
    items: { service_id: number; quantity: number; price: number; discount: number; staff_id: number | null }[];
    note?: string;
}

export interface PosCheckoutBody {
    amount: number;
    payment_method: PosPaymentMethod;
    is_deposit?: boolean;
    voucher_code?: string;
    voucher_discount?: number;
    order_discount?: number;
    rounding_amount?: number;
    final_amount?: number;
    approved_by?: number;
    approved_note?: string;
    transaction_code?: string;
    cashier_name?: string;
    note?: string;
    /** Trả góp: amount phải ≥ đợt 1, is_deposit = false */
    installments?: PosInstallmentInput[];
    /** Bắt buộc khi đợt 1 < 30% tổng đơn (ID Quản lý duyệt) */
    plan_approved_by?: number;
}

export interface PosCheckoutResult extends PosInstallmentInfo {
    allocations?: PosInstallmentAllocation[];
    order_id: number;
    order_code: string;
    appointment_id: number;
    appointment_code: string;
    payment_id: number;
    payment_method: PosPaymentMethod;
    paid_amount: number;
    total_amount: number;
    final_amount: number;
    debt_amount: number;
    payment_status: PosPaymentStatus;
    pos_status: PosStatus;
}

export interface PosOrderPayBody {
    paid_amount: number;
    payment_method: PosPaymentMethod;
    is_deposit?: boolean;
    transaction_code?: string;
    cashier_name?: string;
}

export interface PosOrderPayResult extends PosInstallmentInfo {
    allocations?: PosInstallmentAllocation[];
    order_id: number;
    order_code: string;
    payment_id: number;
    payment_method: PosPaymentMethod;
    amount_paid_this_time: number;
    total_paid: number;
    final_amount: number;
    remaining_debt: number;
    payment_status: PosPaymentStatus;
    pos_status: PosStatus;
}

/** 1b. Đơn đã thu tiền, chờ vào phòng (pos_status = COMPLETED_PAYMENT) */
export interface PosWaitingDispatch {
    order_id: number;
    order_code: string;
    appointment_id: number | null;
    customer_id: number | null;
    customer_name: string | null;
    customer_phone: string | null;
    branch_id: number | null;
    branch_name: string | null;
    primary_service: string | null;
    items_count: number;
    assigned_staffs: string[] | null;
    total_amount: number;
    paid_amount: number;
    debt_amount: number;
    payment_status: PosPaymentStatus;
    pos_status: PosStatus;
    /** "HH:mm:ss DD/MM/YYYY" */
    paid_at: string | null;
    paid_at_iso?: string | null;
}

/** 5. Kết quả đối soát công nợ trước khi vào phòng */
export interface PosCheckinVerify {
    order_id: number;
    order_code: string;
    appointment_id: number | null;
    customer_id: number | null;
    customer_name: string | null;
    customer_phone: string | null;
    debt_balance: number;
    total_amount: number;
    paid_amount: number;
    pos_status: PosStatus;
    payment_status: PosPaymentStatus;
    /** false: còn nợ – chặn vào phòng trừ khi có bảo lãnh */
    allow_service: boolean;
    warning_level: 'SUCCESS' | 'DANGER' | string;
    message: string;
    items: { service_name: string; staff_name: string | null; quantity: number }[];
    /** Trả góp: allow_service xét theo nợ đến hạn, không theo tổng nợ */
    due_amount?: number;
    payment_type?: PosPaymentType;
    next_installment?: PosNextInstallment | null;
    overdue_installments?: { no: number; due_date: string; remaining: number; overdue_days: number }[];
}

/** 6. Kết quả điều phối vào phòng */
export interface PosDispatchResult {
    order_id: number;
    order_code: string;
    pos_status: PosStatus;
    debt_amount: number;
    due_amount?: number;
    payment_type?: PosPaymentType;
    is_guaranteed: boolean;
    dispatched_at: string | null;
    dispatched_at_iso?: string | null;
}

/* ---------- Bán lẻ khách vãng lai (7, V2.2) ---------- */

export interface PosWalkInItem {
    /** package: bán gói liệu trình → server phát hành thẻ (issued_cards) */
    item_type: 'product' | 'service' | 'package';
    product_id?: number;
    service_id?: number;
    package_id?: number;
    /** SKU, mã vạch (code_2) hoặc mã dịch vụ */
    product_code?: string;
    product_name?: string;
    quantity: number;
    price?: number;
    /** Giảm trên MỖI đơn vị */
    discount?: number;
    staff_id?: number | null;
}

/** Tối đa 5 dòng; bán lẻ không có ví trả trước */
export interface PosWalkInPayment {
    method: Exclude<PosPaymentMethod, 'PREPAID' | 'WALLET'>;
    amount: number;
    /** Chỉ tiền mặt: tiền khách đưa (server tính tiền thối) */
    tendered?: number;
    transaction_code?: string;
}

export interface PosWalkInBody {
    /** UUID chống tạo trùng khi gửi lại (mạng chập chờn, bấm 2 lần) */
    client_request_id: string;
    customer_id?: number;
    customer_name?: string;
    /** Bắt buộc khi ghi nợ / trả góp; khách không SĐT phải trả đủ */
    customer_phone?: string;
    branch_id?: number;
    items: PosWalkInItem[];
    payments: PosWalkInPayment[];
    order_discount?: number;
    voucher_code?: string;
    voucher_discount?: number;
    rounding_amount?: number;
    final_amount?: number;
    approved_by?: number;
    approved_note?: string;
    /** Bán gói trả góp: INSTALLMENT + installments (đợt 1 = hôm nay, ≥ 30% hoặc có plan_approved_by) */
    payment_type?: PosPaymentType;
    installments?: PosInstallmentInput[];
    plan_approved_by?: number;
    cashier_id?: number;
    /** Mặc định "Thu ngân POS" */
    cashier_name?: string;
    note?: string;
}

/** Thẻ liệu trình phát hành từ dòng gói của đơn bán lẻ */
export interface PosIssuedCard {
    card_id: number;
    card_code: string;
    package_id: number;
    package_name: string;
    kind: 'session' | 'value';
    total_sessions: number;
    face_value: number;
    price: number;
    expires_at: string;
    order_detail_id: number;
}

export interface PosWalkInResult extends PosInstallmentInfo {
    order_id: number;
    order_code: string;
    issued_cards?: PosIssuedCard[];
    client_request_id?: string;
    /** true: request đã gửi trước đó – trả lại đơn cũ, không tạo đơn mới */
    duplicate?: boolean;
    change_amount?: number;
    pos_status: PosStatus;
    payment_status: PosPaymentStatus;
    final_amount: number;
    paid_amount: number;
    debt_amount: number;
}

/* ---------- Danh sách đơn POS (1c, V2.3) ---------- */

export type PosOrderTab = 'all' | 'completed' | 'debt' | 'cancelled';
export type PosOrderSource = 'appointment' | 'walk_in';

export interface PosOrderListParams {
    /** YYYY-MM-DD (mặc định hôm nay) */
    from?: string;
    to?: string;
    search?: string;
    /** Một id hoặc danh sách "1,3" */
    branch_id?: string;
    tab?: PosOrderTab;
    source?: PosOrderSource;
    /** UNPAID, PARTIALLY_PAID, PAID – nhiều giá trị cách nhau dấu phẩy */
    payment_status?: string;
    payment_type?: PosPaymentType;
    /** CASH, BANK_TRANSFER, ... – nhiều giá trị cách nhau dấu phẩy */
    method?: string;
    cashier?: string;
    page?: number;
    per_page?: number;
}

export interface PosOrderListItem {
    order_id: number;
    order_code: string;
    source: PosOrderSource | string;
    appointment_id: number | null;
    status: string;
    pos_status: PosStatus;
    payment_status: PosPaymentStatus;
    payment_type: PosPaymentType;
    final_amount: number;
    paid_amount: number;
    debt_amount: number;
    customer: { id: number; name: string | null; phone: string | null } | null;
    items_count: number;
    methods: PosPaymentMethod[];
    cashiers: string[];
    created_by_name: string | null;
    branch: { id: number; name: string } | null;
    created_at: string;
    created_at_iso: string;
}

export interface PosOrderList {
    items: PosOrderListItem[];
    tab_counts: Record<PosOrderTab, number>;
    summary: { final_amount: number; paid_amount: number; debt_amount: number };
    pagination: { page: number; per_page: number; total: number; last_page: number };
}

/* ================= HELPERS ================= */

const request = async <T,>(url: string, init: RequestInit = {}): Promise<PosResult<T>> => {
    try {
        const res = await authFetch(url, {
            ...init,
            headers: { Accept: 'application/json', ...(init.body ? { 'Content-Type': 'application/json' } : {}), ...init.headers },
        });
        let json: any = null;
        try {
            json = await res.json();
        } catch {
            return { ok: false, data: null, message: `Máy chủ trả về dữ liệu không hợp lệ (HTTP ${res.status})` };
        }
        const ok = res.ok && json?.success === true;
        return {
            ok,
            data: ok ? (json.data as T) : null,
            message: json?.message || json?.msg || (ok ? '' : `Lỗi máy chủ (HTTP ${res.status})`),
            ...(ok ? {} : { code: json?.code, error: json }),
        };
    } catch (e: any) {
        return { ok: false, data: null, message: e?.message === 'Unauthorized' ? 'Phiên đăng nhập đã hết hạn' : 'Không kết nối được máy chủ' };
    }
};

const qs = (params: Record<string, string | number | undefined | null>) => {
    const p = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') p.append(k, String(v));
    });
    const s = p.toString();
    return s ? `?${s}` : '';
};

const post = <T,>(url: string, body: unknown) => request<T>(url, { method: 'POST', body: JSON.stringify(body) });
const put = <T,>(url: string, body: unknown) => request<T>(url, { method: 'PUT', body: JSON.stringify(body) });

/* ================= API ================= */

/** 1. Danh sách lịch hẹn S7 chờ thanh toán */
export const getPosWaitingOrders = (params: { branch_id?: number; search?: string } = {}) =>
    request<PosWaitingOrder[]>(`/api/v2/pos/waiting-orders${qs(params)}`);

/** 2. Nạp giỏ hàng lịch hẹn */
export const getPosAppointmentCart = (appointmentId: number | string) =>
    request<PosAppointmentCart>(`/api/v2/pos/appointments/${appointmentId}`);

/** 2b. Chi tiết đơn POS (nhận order_id hoặc order_code) */
export const getPosOrder = (orderIdOrCode: number | string) =>
    request<PosOrderDetail>(`/api/v2/pos/orders/${encodeURIComponent(String(orderIdOrCode).trim())}`);

/** 3. Cập nhật dịch vụ / KTV / chiết khấu trên lịch hẹn (chưa tạo đơn) */
export const updatePosAppointmentServices = (appointmentId: number | string, body: PosUpdateServicesBody) =>
    put<PosAppointmentCart>(`/api/v2/pos/appointments/${appointmentId}/services`, body);

/** 4. Thanh toán & tạo đơn từ lịch hẹn (bước duy nhất tạo đơn) */
export const checkoutPosAppointment = (appointmentId: number | string, body: PosCheckoutBody) =>
    post<PosCheckoutResult>(`/api/v2/pos/appointments/${appointmentId}/checkout-and-pay`, body);

/** 4b. Thanh toán thêm / thu nợ cho đơn đã tạo */
export const payPosOrder = (orderId: number | string, body: PosOrderPayBody) =>
    post<PosOrderPayResult>(`/api/v2/pos/orders/${orderId}/pay`, body);

/** 1c. Danh sách đơn POS (lịch hẹn + khách lẻ) theo ngày, tab, nguồn, phương thức… */
export const getPosOrders = (params: PosOrderListParams = {}) =>
    request<PosOrderList>(`/api/v2/pos/orders${qs({ ...params })}`);

/** 7. Bán lẻ: tạo đơn khách vãng lai và thu tiền trong một lượt (gửi lại cùng client_request_id không tạo trùng) */
export const createPosWalkInOrder = (body: PosWalkInBody) => post<PosWalkInResult>('/api/v2/pos/orders', body);

/** 1b. Danh sách đơn đã thu tiền, chờ KTV đón vào phòng */
export const getPosWaitingDispatch = (params: { branch_id?: number; search?: string } = {}) =>
    request<PosWaitingDispatch[]>(`/api/v2/pos/waiting-dispatch${qs(params)}`);

/** 5. Đối soát công nợ trước khi vào phòng (truyền 1 trong 4 khóa) */
export const verifyPosCheckin = (by: { order_id?: number; order_code?: string; appointment_id?: number; customer_id?: number }) =>
    post<PosCheckinVerify>('/api/v2/checkin/verify', by);

/**
 * 6. Điều phối đơn sang phòng dịch vụ (IN_PROGRESS).
 * Còn nợ thì server chặn (422 DEBT_GUARANTEE_REQUIRED) trừ khi có `guaranteed_by` (ID Quản lý bảo lãnh).
 */
export const dispatchPosOrder = (orderId: number | string, body: { guaranteed_by?: number; guarantee_note?: string } = {}) =>
    post<PosDispatchResult>(`/api/v2/orders/${orderId}/dispatch-to-service`, body);
